/**
 * useUsers Hook
 *
 * 사용자 목록을 조회하는 Hook입니다.
 */

import { useState, useEffect } from 'react'
import { userRepository } from '../repositories'
import type { User } from '../types/User'

interface UseUsersReturn {
  /**
   * 사용자 목록 (활성화된 것만)
   */
  users: User[]

  /**
   * 로딩 상태
   */
  loading: boolean

  /**
   * 에러 정보
   */
  error: Error | null

  /**
   * 데이터 재조회
   */
  refetch: () => Promise<void>
}

/**
 * 사용자 목록 조회 Hook
 *
 * 기본적으로 활성화된 사용자만 반환합니다.
 *
 * @returns 사용자 목록, 로딩 상태, 에러, refetch 함수
 *
 * @example
 * ```tsx
 * function UserSelector() {
 *   const { users, loading, error } = useUsers()
 *
 *   if (loading) return <div>로딩 중...</div>
 *   if (error) return <div>에러: {error.message}</div>
 *
 *   return (
 *     <select>
 *       {users.map(user => (
 *         <option key={user.id} value={user.id}>{user.name}</option>
 *       ))}
 *     </select>
 *   )
 * }
 * ```
 */
export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      // 활성화된 사용자만 조회
      const data = await userRepository.findActive()
      setUsers(data)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('사용자 목록을 불러오는데 실패했습니다')
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  }
}
